import { useRef, useState } from 'react';

import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import { ConfirmationDialog, FormDialog } from '~components';
import { useSnackbar } from '~hooks';

import SkcDeviceGroupsDataGrid from '../components/dataGrids/skcDeviceGroupsDataGrid';
import SkcDeviceGroupForm from '../forms/skcDeviceGroupForm/skcDeviceGroupForm';
import { SkcDeviceGroupNew } from '../interfaces/skcDeviceGroup';
import SkcDeviceGroupsService from '../services/skcDeviceGroupsService';

type UserAction =
	| {
			type: 'delete' | 'edit';
			label?: string;
			id: string;
	  }
	| {
			type: 'create' | 'sendCommand';
			label?: never;
			id?: never;
	  };

const skcDeviceGroupService = new SkcDeviceGroupsService();

const SkcDeviceGroupsOverviewContainer = () => {
	const { t } = useTranslation();
	const { enqueueSuccessSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();
	const [userAction, setUserAction] = useState<UserAction | null>(null);
	const dataGridRef = useRef<{ mutate: () => void }>(null);

	const { data: deviceGroupData } = useSWR(
		userAction?.type === 'edit' ? [skcDeviceGroupService.basePath, userAction.id] : null,
		([_, id]) => skcDeviceGroupService.getDeviceGroupById(id)
	);

	const { trigger: triggerCreate, isMutating: isCreateMutating } = useSWRMutation(
		skcDeviceGroupService.basePath,
		(_, { arg }: { arg: SkcDeviceGroupNew }) =>
			skcDeviceGroupService.createCompleteDeviceGroup(arg),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyAdded')} ${t('deviceGroup').toLowerCase()}`);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerEdit, isMutating: isEditMutating } = useSWRMutation(
		userAction?.id ? [skcDeviceGroupService.basePath, userAction.id] : null,
		([_, id], { arg }: { arg: SkcDeviceGroupNew }) =>
			skcDeviceGroupService.updateCompleteDeviceGroup(id, arg, deviceGroupData),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(
					`${t('ui.success.message.updated')} ${t('deviceGroup').toLowerCase()}`
				);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	const { trigger: triggerDelete, isMutating: isDeleteMutating } = useSWRMutation(
		userAction?.id ? [skcDeviceGroupService.basePath, userAction.id] : null,
		([_, id]) => skcDeviceGroupService.deleteDeviceGroup(id),
		{
			onSuccess: () => {
				enqueueSuccessSnackbar(`${t('ui.successfullyDeleted')} ${t('deviceGroup').toLowerCase()}`);
				setUserAction(null);
				dataGridRef.current?.mutate();
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error),
		}
	);

	return (
		<Stack
			sx={{
				height: 1,
			}}
		>
			<SkcDeviceGroupsDataGrid
				ref={dataGridRef}
				onEdit={(value) => setUserAction({ type: 'edit', ...value })}
				onDelete={(value) => setUserAction({ type: 'delete', ...value })}
				onCreate={() => setUserAction({ type: 'create' })}
			/>
			<FormDialog
				title={t(userAction?.type === 'edit' ? 'editResource' : 'addResource', {
					resource: t('deviceGroup'),
				})}
				open={userAction?.type === 'edit' || userAction?.type === 'create'}
				loading={isCreateMutating || isEditMutating}
				onClose={() => setUserAction(null)}
				maxWidth='lg'
				fullWidth
			>
				<SkcDeviceGroupForm
					id={userAction?.id}
					onSubmit={async (data) => {
						if (userAction?.type === 'create') {
							await triggerCreate(data);
						} else if (userAction?.type === 'edit') {
							await triggerEdit(data);
						} else {
							console.error('Useraction not supported', userAction);
						}
					}}
				/>
			</FormDialog>
			<ConfirmationDialog
				loading={isDeleteMutating}
				open={userAction?.type === 'delete'}
				subTitle={t('thisWillDeleteResourceWithName', {
					resource: t('deviceGroup'),
					name: userAction?.label ?? userAction?.id,
				})}
				onConfirm={async () => await triggerDelete()}
				onClose={() => setUserAction(null)}
			/>
		</Stack>
	);
};

export default SkcDeviceGroupsOverviewContainer;
